import React from 'react';

import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';

import '../styles/lookup.scss';
import WastewaterPricing from './WastewaterPricing';
import InfoCard from './InfoCard';
import LookupHero from './LookupHero';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import Dropzone from 'react-dropzone';

function HeroMetaHover({ label, panelLabel, items, renderItem, itemKey }) {
  if (!items?.length) return null;

  return (
    <span className="lookup-hero-meta-hover">
      <button type="button" className="lookup-hero-meta-hover-trigger">{label}</button>
      <div className="lookup-hero-meta-hover-panel" role="tooltip">
        {panelLabel ? <span className="lookup-hero-meta-hover-panel-label">{panelLabel}</span> : null}
        <div className="lookup-hero-meta-hover-chips">
          {items.map((item) => (
            <span key={itemKey(item)} className="product-option">{renderItem(item)}</span>
          ))}
        </div>
      </div>
    </span>
  );
}

class Product extends React.Component {
  state = {
    page: 0,
    rowsPerPage: 10,
    showSdsForm: false,
    sdsUrl: '',
    sdsFile: null,
    sdsError: null,
    sdsDragging: false,
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
  };

  ordersSummary() {
    const orders = this.props.info?.orders || [];
    if (!orders.length) return 'No orders in the past year';

    const latest = [...orders].sort((a, b) => new Date(b.submitted) - new Date(a.submitted))[0];
    return `Latest ${latest.submitted} · ${latest.recipient}`;
  }

  openSdsForm = () => {
    this.setState({
      showSdsForm: true,
      sdsUrl: this.props.info?.sds_link || '',
      sdsFile: null,
      sdsError: null,
      sdsDragging: false,
    });
  };

  closeSdsForm = () => {
    this.setState({
      showSdsForm: false,
      sdsUrl: '',
      sdsFile: null,
      sdsError: null,
      sdsDragging: false,
    });
  };

  setSdsFile = (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      this.setState({ sdsError: 'File is too large. Maximum size is 25MB.', sdsDragging: false });
      return;
    }
    this.setState({ sdsFile: file, sdsError: null, sdsDragging: false });
  };

  submitSds = (event) => {
    event.preventDefault();
    const { sdsUrl, sdsFile } = this.state;
    const productId = this.props.info?.id;
    const trimmed = sdsUrl.trim();

    if (!productId) return;

    if (!sdsFile && !trimmed) {
      this.setState({ sdsError: 'Add a PDF or enter an SDS URL' });
      return;
    }

    if (!sdsFile && trimmed && !/^https?:\/\/.+/i.test(trimmed)) {
      this.setState({ sdsError: 'URL must start with http:// or https://' });
      return;
    }

    this.setState({ sdsError: null });
    this.props.updateProductSds(
      this.props.authToken,
      {
        productId,
        file: sdsFile || null,
        sdsUrl: sdsFile ? null : trimmed,
      },
      () => this.closeSdsForm()
    );
  };

  renderSdsActions() {
    const info = this.props.info;
    const isAdmin = this.props.user?.admin === true;
    const hasSds = info.sds_link?.length > 0;

    return (
      <>
        {hasSds ? (
          <a className="button sds" href={info.sds_link} target="_blank" rel="noopener noreferrer">
            Safety Datasheet
            <WarningAmberIcon className="sds-icon" />
          </a>
        ) : null}
        {isAdmin ? (
          <button type="button" className="button sds-manage" onClick={this.openSdsForm}>
            {hasSds ? 'Update SDS' : 'Add SDS'}
          </button>
        ) : null}
      </>
    );
  }

  renderSdsForm() {
    if (!this.state.showSdsForm) return null;

    const { sdsUrl, sdsFile, sdsError, sdsDragging } = this.state;
    const title = this.props.info?.sds_link ? 'Update safety datasheet' : 'Add safety datasheet';
    const model = this.props.info?.model;

    return (
      <div className="modal-backdrop sds-submit-modal" onClick={this.closeSdsForm}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <form className="sds-submit-form" onSubmit={this.submitSds}>
              <div className="sds-submit-header">
                <div className="sds-submit-heading">
                  <span className="sds-submit-title">{title}</span>
                  {model ? <span className="sds-submit-subtitle">{model}</span> : null}
                </div>
                <button type="button" className="sds-submit-close" onClick={this.closeSdsForm} aria-label="Close">
                  <CloseIcon fontSize="small" />
                </button>
              </div>

              <Dropzone
                accept={{ 'application/pdf': ['.pdf'] }}
                maxFiles={1}
                maxSize={25 * 1024 * 1024}
                onDragEnter={() => this.setState({ sdsDragging: true })}
                onDragLeave={() => this.setState({ sdsDragging: false })}
                onDrop={(accepted, rejected) => {
                  if (rejected?.length) {
                    this.setState({
                      sdsError: 'Please upload a PDF under 25MB',
                      sdsDragging: false,
                    });
                    return;
                  }
                  this.setSdsFile(accepted[0]);
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps()}
                    className={`sds-dropzone${sdsDragging ? ' active' : ''}${sdsFile ? ' has-file' : ''}`}
                  >
                    <input {...getInputProps()} />
                    <UploadFileIcon className="sds-dropzone-icon" />
                    {sdsFile ? (
                      <div className="sds-dropzone-copy">
                        <span className="sds-dropzone-primary">{sdsFile.name}</span>
                        <span className="sds-dropzone-secondary">Click or drop to replace</span>
                      </div>
                    ) : (
                      <div className="sds-dropzone-copy">
                        <span className="sds-dropzone-primary">
                          {sdsDragging ? 'Drop PDF here' : 'Drag PDF here or click to upload'}
                        </span>
                        <span className="sds-dropzone-secondary">PDF up to 25MB</span>
                      </div>
                    )}
                  </div>
                )}
              </Dropzone>

              <div className="sds-or-divider"><span>or</span></div>

              <label className="sds-field">
                <span>SDS URL</span>
                <input
                  type="url"
                  value={sdsUrl}
                  placeholder="https://…"
                  onChange={(e) => this.setState({ sdsUrl: e.target.value, sdsError: null })}
                />
              </label>

              {sdsError ? <p className="sds-submit-error">{sdsError}</p> : null}

              <div className="sds-submit-actions">
                <button type="submit" className="sds-submit-button">
                  Submit changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.props.info?.type !== 'product') return null;

    const info = this.props.info;
    const sizes = info.size_options?.replace(/\[|\]/g, '').split(',').filter((s) => s.length > 0) || [];
    const orders = info.orders || [];
    const yearAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toLocaleDateString();
    const isAdmin = this.props.user?.admin === true;
    const hasSdsActions = info.sds_link?.length > 0 || isAdmin;

    return (
      <>
        <div className="info-container">
          <LookupHero
            title={info.model}
            subtitle={info.description}
            image={info.img_link}
            imageAlt={info.model}
            meta={(
              <>
                <span>Vendor: <b>{info.vendor?.name}</b></span>
                {info.variants?.length > 0 ? (
                  <HeroMetaHover
                    label={`${info.variants.length} option${info.variants.length === 1 ? '' : 's'}`}
                    panelLabel="Available options"
                    items={info.variants}
                    itemKey={(variant) => variant.id || variant.code}
                    renderItem={(variant) => variant.code}
                  />
                ) : null}
                {sizes.length > 0 ? (
                  <HeroMetaHover
                    label={`${sizes.length} size${sizes.length === 1 ? '' : 's'}`}
                    panelLabel={info.size_unit ? `Available sizes (${info.size_unit})` : 'Available sizes'}
                    items={sizes}
                    itemKey={(size) => size}
                    renderItem={(size) => size}
                  />
                ) : null}
              </>
            )}
            actions={hasSdsActions ? this.renderSdsActions() : null}
          />

          <InfoCard
            id="product-orders"
            icon={<ShoppingCartIcon />}
            title="Order history"
            count={orders.length}
            subtitle={`Since ${yearAgo}`}
            summary={this.ordersSummary()}
          >
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="product orders" size="medium">
                <TableBody>
                  {[...orders]
                    .sort((a, b) => new Date(b.submitted) - new Date(a.submitted))
                    .slice(this.state.page * this.state.rowsPerPage, this.state.page * this.state.rowsPerPage + this.state.rowsPerPage)
                    .map((row) => (
                      <TableRow
                        key={`order-${row.url}`}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                        onClick={() => window.open(row.url, '_blank', 'noopener,noreferrer')}
                      >
                        <TableCell align="left"><b>{row.recipient}</b></TableCell>
                        <TableCell align="left">{row.placed_by}</TableCell>
                        <TableCell align="right">{row.submitted}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={orders.length}
              rowsPerPage={this.state.rowsPerPage}
              page={this.state.page}
              onPageChange={this.handleChangePage}
              onRowsPerPageChange={this.handleChangeRowsPerPage}
            />
          </InfoCard>

          <WastewaterPricing contextType="p" />
        </div>

        {this.renderSdsForm()}
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    info: state.info,
    user: state.user,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Product);
